import 'react-dates/initialize';
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import compose from 'recompose/compose';
import { withRouter } from 'react-router-dom';
import { closeModal } from 'src/actions/modals';

import { withCurrentUser } from 'src/components/withCurrentUser';
import { Avatar } from 'src/components/avatar';
import { ErrorBoundary } from 'src/components/error';
import ModalContainer from '../modalContainer';
import { PrimaryButton } from 'src/components/button';
import Tooltip from 'src/components/tooltip';
import { modalStyles } from '../styles';
import publishEvent from 'shared/graphql/mutations/event/publishEvent';

import { TextArea, Error as ErrorElement, Input } from '../../formElements';
import { addToastWithTimeout } from 'src/actions/toasts';
import NextPageButton from 'src/components/nextPageButton';
import EmojiPicker from 'src/components/emojiPicker';
import getPresignedPutUrl from 'src/helpers/uploadFile';
import Icon from 'src/components/icon';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {
  Form,
  Actions,
  ModeratorSelection,
  SelectOption,
  SquareButton,
  PreviewMedia,
  MediaLabel,
  RemovePreviewButton,
  MediaInput,
  Label,
} from './style';
import MediaUploader from 'src/components/mediaUploader';
import processThreadContent from 'shared/draft-utils/process-thread-content';

import dataProvider from 'shared/rest';

import { debounce } from 'lodash';

const DATA_TEST_PRE = 'components-modals-EditEventModal-';

const EditEventModal = props => {
  const { isOpen, dispatch, history, event, setEvent: setDetailEvent } = props;
  const styles = modalStyles(720);

  const [loading, setLoading] = useState(false);
  const [editError, setEditError] = useState(false);
  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState(event.description);

  const [nameError, setNameError] = useState();
  const [descriptionError, setDescriptionError] = useState();
  const [previewMedia, setPreviewMedia] = useState(event.bannerImage);
  const [bannerImageFile, setBannerImageFile] = useState();
  const [bannerImageError, setBannerImageError] = useState();

  const fileInputRef = useRef(null);

  const onSelectFile = file => {
    setPreviewMedia(window.URL.createObjectURL(file));
    setBannerImageFile(file);
  };

  const selectImage = e => {
    debounce(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 500);
  };

  const close = () => dispatch(closeModal());
  const create = async e => {
    e.preventDefault();
    setLoading(true);
    if (!name || name.length > 250 || !description || !previewMedia) {
      if (!name) {
        setNameError('required');
      }
      if (name.length > 250) {
        setNameError('cannot be longer than 250 characters');
      }
      if (!description) {
        setDescriptionError('required');
      }
      if (!previewMedia) {
        setBannerImageError('Please add an event banner image.');
      }
      setEditError(true);
      setLoading(false);
      return;
    }

    let bannerImage;
    if (bannerImageFile) {
      bannerImage = await getPresignedPutUrl({
        location: 'post',
        file: bannerImageFile,
      });
    }

    const params = {
      eventId: event.id,
      newEvent: {
        name,
        description,
        bannerImage,
      },
    };
    const result = (await dataProvider.invoke('events', 'editEvent', params))
      .data;

    if (result.ok) {
      dispatch(addToastWithTimeout('success', 'Edited A New Event'));
      setDetailEvent({
        ...event,
        name,
        description: description,
        bannerImage: previewMedia,
        processedDescription: processThreadContent('TEXT', description),
      });
      setLoading(false);
      props.dispatch(closeModal());
    } else {
      setLoading(false);
      props.dispatch(
        addToastWithTimeout(
          'error',
          result.message || 'Edit event failed',
          10000
        )
      );
      props.dispatch(closeModal());
    }
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      style={styles}
      closeTimeoutMS={330}
    >
      <ModalContainer title={'Edit an Event'} closeModal={close}>
        <Form>
          <TextArea
            testId={`${DATA_TEST_PRE}Name-TextArea`}
            id="name"
            placeholder="What's this event name?"
            defaultValue={name}
            onChange={e => setName(e.target.value)}
          >
            Name
          </TextArea>
          <ErrorElement>{nameError}</ErrorElement>
          <TextArea
            testId={`${DATA_TEST_PRE}Description-TextArea`}
            id="description"
            defaultValue={description}
            placeholder="Tell the audience more about the events to attract their attention."
            onChange={e => setDescription(e.target.value)}
          >
            Description
          </TextArea>
          <ErrorElement>{descriptionError}</ErrorElement>
          <Label>Banner Image</Label>
          {previewMedia && (
            <PreviewMedia>
              <img src={previewMedia} alt="" />
              <RemovePreviewButton
                data-testid={`${DATA_TEST_PRE}Remove-Preview-Button`}
                onClick={() => setPreviewMedia()}
              >
                <Icon glyph="view-close-small" size={'16'} />
              </RemovePreviewButton>
            </PreviewMedia>
          )}
          <ErrorElement>{bannerImageError}</ErrorElement>
          <SquareButton
            type="button"
            onClick={selectImage}
            data-testid={`${DATA_TEST_PRE}Upload-Photo-Button`}
          >
            <MediaUploader
              icon="add"
              color="#fff"
              size={42}
              isSendingMediaMessage={false}
              tooltipContent="Upload photo"
              onValidated={onSelectFile}
              currentUser={props.currentUser}
              onError={err => console.log(err)}
            />
            <Tooltip content={'Upload photo'}>
              <MediaLabel>
                <MediaInput
                  ref={fileInputRef}
                  type="file"
                  accept={'.png, .jpg, .jpeg, .gif, .mp4'}
                  multiple={false}
                />
              </MediaLabel>
            </Tooltip>
          </SquareButton>

          <Actions>
            <PrimaryButton
              disabled={false}
              onClick={create}
              data-testid={`${DATA_TEST_PRE}Submit-Button`}
            >
              {loading ? 'Edit...' : 'EDIT'}
            </PrimaryButton>
          </Actions>

          {editError && (
            <ErrorElement>
              Please fix any errors above before editing this event.
            </ErrorElement>
          )}
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const map = state => ({
  isOpen: state.modals.isOpen,
});

export default compose(
  // $FlowIssue
  connect(map),
  publishEvent,
  withRouter
)(EditEventModal);
